import * as React from 'react'
import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Checkbox, Dialog, FormControlLabel, makeStyles } from '@material-ui/core'
import { useTranslate } from 'react-admin'
import ButtonWithLoader from '../ui/ButtonWithLoader/ButtonWithLoader'
import LoaderDots from '../loaders/LoaderDots/LoaderDots'
import contractSigningDialogStyles from './contractSigningDialog.styles'
import useIsMobileScreen from '../../hooks/useIsMobileScreen'

/**
 * Full-screen view of the archived contract PDF with a single Accept. Not dismissable (like
 * ConsentPopup): declining is walking away, which leaves the invitation pending.
 *
 * @param {boolean} open
 * @param {{ documentHash: string, pdf: Blob } | null} contract  null shows a loader
 * @param {boolean} accepting
 * @param {(documentHash: string) => void} onAccept  handed the hash of the document displayed
 * @param {string} [errorMessage]
 * @param {string} [subtitle]  defaults to the kick-off wording
 */
const ContractSigningDialog = ({ open, contract, accepting, errorMessage, onAccept, subtitle }) => {
  const translate = useTranslate()
  const useStyles = makeStyles(contractSigningDialogStyles)
  const classes = useStyles()
  const isMobileScreen = useIsMobileScreen()

  const [documentUrl, setDocumentUrl] = useState('')
  const [acknowledged, setAcknowledged] = useState(false)

  const pdf = contract?.pdf
  useEffect(() => {
    if (!pdf) {
      setDocumentUrl('')

      return undefined
    }

    const url = URL.createObjectURL(pdf)
    setDocumentUrl(url)
    setAcknowledged(false)

    return () => URL.revokeObjectURL(url)
  }, [pdf])

  if (!open) return null

  const title = translate('ra.kickoffContract.title')

  return (
    <Dialog
      open={open}
      fullScreen
      disableScrollLock
      disableEscapeKeyDown
      classes={{ paper: classes.paper }}
    >
      <div className={classes.container}>
        <div className={classes.title}>{title}</div>
        <div className={classes.subtitle}>
          {subtitle || translate('ra.kickoffContract.subtitle')}
        </div>

        {contract && documentUrl ? (
          <>
            {isMobileScreen ? (
              // Phone browsers don't render a PDF inside a page (Android shows an empty frame, iOS
              // only the first page), so the signer opens it in the phone's own viewer instead. A
              // blob URL in a new tab is blank on Android; downloading it hands it to the viewer.
              <div className={classes.mobileDocument}>
                <a
                  className={classes.mobileOpenButton}
                  href={documentUrl}
                  download={`${title}.pdf`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {translate('ra.kickoffContract.openDocument')}
                </a>
              </div>
            ) : (
              <>
                <iframe src={documentUrl} title={title} className={classes.document} />
                {/* Some browsers refuse to render a PDF inline; without this the signer would face an empty frame. */}
                <a
                  className={classes.openInTab}
                  href={documentUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {translate('ra.kickoffContract.openInNewTab')}
                </a>
              </>
            )}
            {errorMessage ? <div className={classes.error}>{errorMessage}</div> : null}
            <div className={classes.buttonWrapper}>
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    disabled={accepting}
                  />
                }
                label={translate('ra.kickoffContract.acknowledge')}
              />
              <ButtonWithLoader
                type='button'
                label={translate('ra.kickoffContract.accept')}
                loading={accepting}
                disabled={!acknowledged}
                onClick={() => onAccept(contract.documentHash)}
              />
            </div>
          </>
        ) : (
          <div className={classes.loaderWrapper}>
            <LoaderDots />
          </div>
        )}
      </div>
    </Dialog>
  )
}

ContractSigningDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  contract: PropTypes.shape({
    documentHash: PropTypes.string.isRequired,
    pdf: PropTypes.instanceOf(Blob).isRequired,
  }),
  accepting: PropTypes.bool,
  errorMessage: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
}

export default ContractSigningDialog
